import React, { useState, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  AreaChart,
} from "recharts"
import api from "../../services/api"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Skeleton } from "../ui/skeleton"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart"
import { Target } from "lucide-react"

const dayOfWeekConfig = {
  workouts: {
    label: "Workouts",
    color: "hsl(var(--chart-1))",
  },
}

const hourlyConfig = {
  workouts: {
    label: "Workouts",
    color: "hsl(var(--chart-2))",
  },
}

function AdditionalInsights({ adminOnly }) {
  const [activeView, setActiveView] = useState("dayOfWeek")

  const calculateAvgSetsPerWorkout = useCallback((workoutsList) => {
    if (!workoutsList?.length) return 0
    let totalSets = 0
    workoutsList.forEach((workout) => {
      if (workout.blocks) {
        workout.blocks.forEach((block) => {
          if (block.exercises) {
            block.exercises.forEach((exercise) => {
              totalSets += exercise.sets?.length || 0
            })
          }
        })
      }
    })
    return (totalSets / workoutsList.length).toFixed(1)
  }, [])

  const { data: insights, isLoading } = useQuery({
    queryKey: ["insights", adminOnly],
    queryFn: async () => {
      const stats = await api.getStats(adminOnly)
      const workoutsData = await api.getWorkouts({ limit: 100, adminOnly })

      const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0]
      const hourCounts = new Array(24).fill(0)

      const workouts = workoutsData.workouts || []
      workouts.forEach((workout) => {
        if (workout.workoutStartTime) {
          const date = new Date(workout.workoutStartTime)
          dayOfWeekCounts[date.getDay()]++
          hourCounts[date.getHours()]++
        }
      })

      return {
        dayOfWeek: dayOfWeekCounts,
        hourly: hourCounts,
        totalWorkouts: stats.totalWorkouts,
        avgSetsPerWorkout: calculateAvgSetsPerWorkout(workouts),
      }
    },
    staleTime: 60000,
  })

  const dayOfWeekData = React.useMemo(() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    return days.map((day, index) => ({
      day,
      fullDay: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][index],
      workouts: insights?.dayOfWeek?.[index] || 0,
    }))
  }, [insights?.dayOfWeek])

  const hourlyData = React.useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const hour = i === 0 ? 12 : i > 12 ? i - 12 : i
      const ampm = i < 12 ? "AM" : "PM"
      return {
        hour: `${hour}${ampm}`,
        fullHour: `${i}:00`,
        workouts: insights?.hourly?.[i] || 0,
      }
    })
  }, [insights?.hourly])

  const mostActiveDay = React.useMemo(() => {
    if (!insights?.dayOfWeek) return "-"
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const maxIndex = insights.dayOfWeek.indexOf(Math.max(...insights.dayOfWeek))
    return days[maxIndex]
  }, [insights?.dayOfWeek])

  const peakHour = React.useMemo(() => {
    if (!insights?.hourly) return "-"
    const maxIndex = insights.hourly.indexOf(Math.max(...insights.hourly))
    return `${maxIndex}:00`
  }, [insights?.hourly])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Additional Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
            <Skeleton className="h-[300px]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Additional Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold tabular-nums">
                {insights?.avgSetsPerWorkout || 0}
              </p>
              <p className="text-xs text-muted-foreground">Avg Sets/Workout</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{mostActiveDay}</p>
              <p className="text-xs text-muted-foreground">Most Active Day</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold tabular-nums">{peakHour}</p>
              <p className="text-xs text-muted-foreground">Peak Hour</p>
            </CardContent>
          </Card>
        </div>

        {/* Chart Tabs */}
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="mb-4">
            <TabsTrigger value="dayOfWeek">By Day of Week</TabsTrigger>
            <TabsTrigger value="hourly">By Hour</TabsTrigger>
          </TabsList>

          <TabsContent value="dayOfWeek">
            <ChartContainer config={dayOfWeekConfig} className="h-[250px] w-full">
              <BarChart data={dayOfWeekData} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                <ChartTooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value, payload) => payload?.[0]?.payload?.fullDay || value}
                    />
                  }
                />
                <Bar dataKey="workouts" fill="var(--color-workouts)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </TabsContent>

          <TabsContent value="hourly">
            <ChartContainer config={hourlyConfig} className="h-[250px] w-full">
              <AreaChart data={hourlyData} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="hour"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  interval={2}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                <ChartTooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value, payload) => payload?.[0]?.payload?.fullHour || value}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="workouts"
                  stroke="var(--color-workouts)"
                  fill="var(--color-workouts)"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ChartContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

export default AdditionalInsights
